import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Flex } from 'components/ui';
import {
    colors, space, fontSizes, fontWeights, mediaQueries
} from 'style/config';

/**
 * Calculate progress fill percentage for the current segment
 * @param {number} currentSegmentIndex - Current segment index (0, 1, or 2)
 * @param {number} currentValue - Current value (e.g., spending, points)
 * @param {number} middleThreshold - Threshold for middle segment
 * @param {number} maxThreshold - Maximum threshold value
 * @param {number} middleMarkerPosition - Position of middle marker (percentage)
 * @returns {object} Progress data including fillPercentage and isValid
 */
const calculateProgress = (currentSegmentIndex, currentValue, middleThreshold, maxThreshold, middleMarkerPosition) => {
    // Validate inputs
    if (!middleThreshold || !maxThreshold || middleThreshold <= 0 || maxThreshold <= 0) {
        return {
            fillPercentage: 0,
            isValid: false
        };
    }

    const value = currentValue !== null && currentValue !== undefined ? currentValue : 0;

    if (currentSegmentIndex === 0) {
        // Segment 0: Progress from 0% to middleMarkerPosition%
        if (value === 0) {
            return {
                fillPercentage: 0,
                isValid: true
            };
        } else {
            const progressPercentage = (value / middleThreshold) * middleMarkerPosition;

            return {
                fillPercentage: Math.min(progressPercentage, middleMarkerPosition),
                isValid: true
            };
        }
    } else if (currentSegmentIndex === 1) {
        // Segment 1: Progress from middleMarkerPosition% to 100%
        if (value === 0) {
            return {
                fillPercentage: middleMarkerPosition,
                isValid: true
            };
        } else {
            const progressInSegment = value - middleThreshold;
            const segmentRange = maxThreshold - middleThreshold;
            const segmentProgress = (progressInSegment / segmentRange) * (100 - middleMarkerPosition);
            const fillPercentage = middleMarkerPosition + segmentProgress;

            return {
                fillPercentage: Math.min(fillPercentage, 100),
                isValid: true
            };
        }
    } else if (currentSegmentIndex === 2) {
        // Segment 2: Complete, always 100% (value doesn't matter)
        return {
            fillPercentage: 100,
            isValid: true
        };
    }

    // Fallback for unknown segment
    return {
        fillPercentage: 0,
        isValid: false
    };
};

/**
 * Get marker colors based on current segment
 */
const getMarkerColors = (currentSegmentIndex, isValid) => {
    if (!isValid) {
        return {
            startMarkerColor: colors.midGray,
            middleMarkerColor: colors.midGray,
            endMarkerColor: colors.midGray
        };
    }

    const progressColor = colors.red;
    const baseColor = colors.midGray;

    return {
        startMarkerColor: progressColor, // Always filled (start point)
        middleMarkerColor: currentSegmentIndex >= 1 ? progressColor : baseColor,
        endMarkerColor: currentSegmentIndex >= 2 ? progressColor : baseColor
    };
};

/**
 * Get label style based on whether it's active
 */
const getLabelStyle = (isActive, additionalStyles = {}, isValid = true) => {
    return {
        ...additionalStyles,
        fontWeight: isActive && isValid ? fontWeights.bold : fontWeights.normal,
        color: isActive && isValid ? colors.black : colors.gray,
        lineHeight: '18px'
    };
};

/**
 * MultiSegmentProgressBar - Displays progress across multiple thresholds
 * Shows dynamic progress bar with three segments/markers
 *
 * @param {number} currentSegmentIndex - Current segment (0, 1, or 2)
 * @param {number} currentValue - Current progress value
 * @param {number} middleThreshold - Threshold for middle segment
 * @param {number} maxThreshold - Maximum threshold value
 * @param {array} segmentLabels - Labels for each segment [start, middle, end]
 * @param {string} progressLabel - Aria label for progress bar
 */
const MultiSegmentProgressBar = ({
    currentSegmentIndex,
    currentValue,
    middleThreshold,
    maxThreshold,
    segmentLabels = ['Start', 'Middle', 'End'],
    progressLabel = 'Progress'
}) => {
    // Calculate middle marker position (dynamic, NOT centered at 50%)
    const middleMarkerPosition = (middleThreshold / maxThreshold) * 100;

    // Calculate progress
    const { fillPercentage, isValid } = calculateProgress(currentSegmentIndex, currentValue, middleThreshold, maxThreshold, middleMarkerPosition);

    const { startMarkerColor, middleMarkerColor, endMarkerColor } = getMarkerColors(currentSegmentIndex, isValid);

    return (
        <Box marginTop={4}>
            {/* Progress Bar Container */}
            <Box css={styles.progressBarWrapper}>
                <Box css={styles.progressBarContainer}>
                    {/* Progress Fill */}
                    <Box
                        style={{
                            width: `${fillPercentage}%`,
                            backgroundColor: colors.red
                        }}
                        css={styles.progressBarFill}
                        role='progressbar'
                        aria-valuenow={Math.round(fillPercentage)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${progressLabel}: ${Math.round(fillPercentage)}%`}
                    />

                    {/* Segment Markers */}
                    <Box
                        css={styles.marker}
                        style={{
                            left: '0%',
                            backgroundColor: startMarkerColor,
                            transform: 'translate(0, -50%)'
                        }}
                        aria-hidden='true'
                    />
                    <Box
                        css={styles.marker}
                        style={{
                            left: `${isValid ? middleMarkerPosition : INVALID_MIDDLE_MARKER_POSITION}%`,
                            backgroundColor: middleMarkerColor
                        }}
                        aria-hidden='true'
                    />
                    <Box
                        css={styles.marker}
                        style={{
                            left: '100%',
                            backgroundColor: endMarkerColor,
                            transform: 'translate(-100%, -50%)'
                        }}
                        aria-hidden='true'
                    />
                </Box>
            </Box>

            {/* Segment Labels */}
            <Flex css={styles.labelsContainer}>
                <Text
                    css={styles.label}
                    style={getLabelStyle(currentSegmentIndex === 0, { textAlign: 'left' }, isValid)}
                >
                    {segmentLabels[0]}
                </Text>
                <Text
                    css={styles.label}
                    style={getLabelStyle(
                        currentSegmentIndex === 1,
                        {
                            position: 'absolute',
                            left: `${isValid ? middleMarkerPosition : INVALID_MIDDLE_MARKER_POSITION}%`,
                            transform: 'translateX(-50%)'
                        },
                        isValid
                    )}
                >
                    {segmentLabels[1]}
                </Text>
                <Text
                    css={styles.label}
                    style={getLabelStyle(currentSegmentIndex === 2, { textAlign: 'right' }, isValid)}
                >
                    {segmentLabels[2]}
                </Text>
            </Flex>
        </Box>
    );
};

const INVALID_MIDDLE_MARKER_POSITION = 35;

const styles = {
    progressBarWrapper: {
        position: 'relative',
        width: '100%'
    },
    progressBarContainer: {
        position: 'relative',
        backgroundColor: colors.midGray,
        borderRadius: 20,
        height: space[1],
        width: '100%',
        overflow: 'visible',
        [mediaQueries.smMax]: {
            height: space[1]
        }
    },
    progressBarFill: {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        borderRadius: 20,
        transition: 'width 0.3s ease-in-out'
    },
    marker: {
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: space[2],
        height: space[2],
        borderRadius: '50%'
    },
    labelsContainer: {
        position: 'relative',
        marginTop: space[2],
        justifyContent: 'space-between',
        [mediaQueries.smMax]: {
            marginTop: space[1]
        }
    },
    label: {
        fontSize: fontSizes.base,
        fontStyle: 'normal',
        [mediaQueries.smMax]: {
            fontSize: fontSizes.sm
        }
    }
};

MultiSegmentProgressBar.propTypes = {
    currentSegmentIndex: PropTypes.number.isRequired,
    currentValue: PropTypes.number,
    middleThreshold: PropTypes.number,
    maxThreshold: PropTypes.number,
    segmentLabels: PropTypes.arrayOf(PropTypes.string),
    progressLabel: PropTypes.string
};

export default wrapFunctionalComponent(MultiSegmentProgressBar, 'MultiSegmentProgressBar');
